import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Mail, Server, Send, Plus, Edit, Trash2, Loader2, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import { MultiSearchableSelect } from '../components/ui/searchable-select';

const SMTPSettingsPage = () => {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // SMTP Settings
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    from_name: '',
    use_tls: true,
    configured: false
  });
  
  // Email Templates
  const [templates, setTemplates] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ 
    name: '', 
    subject: '', 
    body: '',
    university_id: null,
    template_type: null,
    trigger_status_ids: [],
    trigger_status_names: [],
  });

  // r66.12 — Variable insertion + Status-trigger
  // Available {{variables}} the user can drop into subject/body. Keep this in
  // sync with the application columns visible in the Applications table.
  const TEMPLATE_VARIABLES = [
    { key: 'app_number', label: 'App Number' },
    { key: 'student_name', label: 'Student Name' },
    { key: 'student_email', label: 'Student Email' },
    { key: 'student_phone', label: 'Student Phone' },
    { key: 'country', label: 'Country' },
    { key: 'university_name', label: 'University' },
    { key: 'program_name', label: 'Program' },
    { key: 'intake_name', label: 'Intake' },
    { key: 'application_type', label: 'Application Type' },
    { key: 'case_owner_name', label: 'Case Owner' },
    { key: 'current_desk_name', label: 'Current Desk' },
    { key: 'status', label: 'Status' },
    { key: 'today', label: "Today's Date" },
    { key: 'year', label: 'Current Year' },
  ];
  const [statusOptions, setStatusOptions] = useState([]);
  // refs for cursor-aware insertion into the subject / body fields
  const subjectRef = React.useRef(null);
  const bodyRef = React.useRef(null);
  const insertVariable = (field, varKey) => {
    if (!varKey) return;
    const ref = field === 'subject' ? subjectRef.current : bodyRef.current;
    const current = templateForm[field] || '';
    const token = `{{${varKey}}}`;
    if (ref && typeof ref.selectionStart === 'number') {
      const start = ref.selectionStart;
      const end = ref.selectionEnd;
      const next = current.slice(0, start) + token + current.slice(end);
      setTemplateForm((prev) => ({ ...prev, [field]: next }));
      // restore caret after the inserted token on the next tick
      setTimeout(() => {
        try {
          ref.focus();
          const pos = start + token.length;
          ref.setSelectionRange(pos, pos);
        } catch { /* ignore */ }
      }, 0);
    } else {
      setTemplateForm((prev) => ({ ...prev, [field]: (current || '') + token }));
    }
  };
  const toggleTriggerStatus = (statusId, statusName) => {
    setTemplateForm((prev) => {
      const ids = new Set(prev.trigger_status_ids || []);
      const names = new Set(prev.trigger_status_names || []);
      if (ids.has(statusId)) {
        ids.delete(statusId);
        names.delete(statusName);
      } else {
        ids.add(statusId);
        names.add(statusName);
      }
      return {
        ...prev,
        trigger_status_ids: Array.from(ids),
        trigger_status_names: Array.from(names),
      };
    });
  };
  
  // Universities for template mapping
  const [universities, setUniversities] = useState([]);
  
  // Template types
  const TEMPLATE_TYPES = [
    { value: 'welcome', label: 'Welcome Email', description: 'Sent when application is received' },
    { value: 'document_request', label: 'Document Request', description: 'Request missing documents' },
    { value: 'reminder', label: 'Document Reminder', description: 'Remind about promised documents' },
    { value: 'status_update', label: 'Status Update', description: 'Application status change' },
    { value: 'custom', label: 'Custom', description: 'General purpose template' }
  ];
  
  // Sent Emails
  const [sentEmails, setSentEmails] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, templatesRes, emailsRes, uniRes, statusRes] = await Promise.all([
        api().get('/smtp-settings'),
        api().get('/email-templates'),
        api().get('/sent-emails'),
        api().get('/master-data?type=university'),
        api().get('/master-data?type=application_status'),
      ]);
      
      if (settingsRes.data.configured) {
        setSmtpSettings({ ...settingsRes.data, smtp_password: '' });
      }
      setTemplates(templatesRes.data);
      setSentEmails(emailsRes.data);
      setUniversities(uniRes.data || []);
      setStatusOptions(statusRes.data || []);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!smtpSettings.smtp_host || !smtpSettings.smtp_username || !smtpSettings.from_email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      await api().put('/smtp-settings', smtpSettings);
      toast.success('SMTP settings saved successfully');
      setSmtpSettings({ ...smtpSettings, configured: true });
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSettings = async () => {
    if (!smtpSettings.smtp_host || !smtpSettings.smtp_username || !smtpSettings.smtp_password) {
      toast.error('Please fill in all fields including password to test');
      return;
    }
    
    setTesting(true);
    try {
      await api().post('/smtp-settings/test', smtpSettings);
      toast.success('Test email sent successfully! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SMTP test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.body) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      if (editingTemplate) {
        await api().put(`/email-templates/${editingTemplate.id}`, templateForm);
        toast.success('Template updated');
      } else {
        await api().post('/email-templates', templateForm);
        toast.success('Template created');
      }
      setTemplateDialogOpen(false);
      setEditingTemplate(null);
      setTemplateForm({ name: '', subject: '', body: '', university_id: null, template_type: null, trigger_status_ids: [], trigger_status_names: [] });
      fetchData();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await api().delete(`/email-templates/${templateId}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({ 
      name: template.name, 
      subject: template.subject, 
      body: template.body,
      university_id: template.university_id || null,
      template_type: template.template_type || null,
      trigger_status_ids: template.trigger_status_ids || [],
      trigger_status_names: template.trigger_status_names || [],
    });
    setTemplateDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="smtp-settings-page">
      <div>
        <h1 className="text-2xl font-bold">SMTP Settings</h1>
        <p className="text-muted-foreground">Configure your email settings to send emails to students</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Server className="w-4 h-4 mr-2" />
            SMTP Configuration
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">
            <Mail className="w-4 h-4 mr-2" />
            Email Templates
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <Send className="w-4 h-4 mr-2" />
            Sent Emails
          </TabsTrigger>
        </TabsList>

        {/* SMTP Configuration Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="w-5 h-5" />
                SMTP Server Configuration
              </CardTitle>
              <CardDescription>
                Configure your SMTP server to send emails. Common providers: Gmail (smtp.gmail.com:587), Outlook (smtp.office365.com:587)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Provider Presets — one click fills the SMTP host/port/username for common providers */}
              <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4" data-testid="smtp-provider-presets">
                <p className="text-sm font-medium text-gray-700 mb-2">Quick setup — pick your provider</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'sendgrid', label: 'SendGrid', cfg: { smtp_host: 'smtp.sendgrid.net', smtp_port: 587, smtp_username: 'apikey', use_tls: true } },
                    { id: 'gmail', label: 'Gmail', cfg: { smtp_host: 'smtp.gmail.com', smtp_port: 587, use_tls: true } },
                    { id: 'outlook', label: 'Outlook 365', cfg: { smtp_host: 'smtp.office365.com', smtp_port: 587, use_tls: true } },
                    { id: 'mailgun', label: 'Mailgun', cfg: { smtp_host: 'smtp.mailgun.org', smtp_port: 587, use_tls: true } },
                    { id: 'ses', label: 'AWS SES', cfg: { smtp_host: 'email-smtp.us-east-1.amazonaws.com', smtp_port: 587, use_tls: true } },
                    { id: 'cloudlab', label: 'Cloud Lab', cfg: { smtp_host: 'mail.cloudlabweb.com', smtp_port: 465, use_tls: true } },
                  ].map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSmtpSettings({ ...smtpSettings, ...p.cfg })}
                      className="px-3 py-1.5 text-xs font-medium rounded-full border border-gray-200 bg-white hover:border-[#F5A623] hover:text-[#F5A623] transition"
                      data-testid={`smtp-preset-${p.id}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  <strong>SendGrid:</strong> use <code className="bg-gray-200 px-1 rounded">apikey</code> as username and paste your <code className="bg-gray-200 px-1 rounded">SG.xxx</code> API key as the password.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="smtp_host">SMTP Host *</Label>
                  <Input
                    id="smtp_host"
                    placeholder="smtp.gmail.com"
                    value={smtpSettings.smtp_host}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_host: e.target.value })}
                    data-testid="smtp-host-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp_port">SMTP Port *</Label>
                  <Input
                    id="smtp_port"
                    type="number"
                    placeholder="587"
                    value={smtpSettings.smtp_port}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_port: parseInt(e.target.value) || 587 })}
                    data-testid="smtp-port-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp_username">Username/Email *</Label>
                  <Input
                    id="smtp_username"
                    placeholder="your-email@gmail.com"
                    value={smtpSettings.smtp_username}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_username: e.target.value })}
                    data-testid="smtp-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp_password">Password/App Password *</Label>
                  <div className="relative">
                    <Input
                      id="smtp_password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={smtpSettings.smtp_password}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_password: e.target.value })}
                      data-testid="smtp-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">For Gmail, use an App Password instead of your regular password</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="from_email">From Email *</Label>
                  <Input
                    id="from_email"
                    placeholder="noreply@yourcompany.com"
                    value={smtpSettings.from_email}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, from_email: e.target.value })}
                    data-testid="from-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="from_name">From Name *</Label>
                  <Input
                    id="from_name"
                    placeholder="Your Company Name"
                    value={smtpSettings.from_name}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, from_name: e.target.value })}
                    data-testid="from-name-input"
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="use_tls"
                  checked={smtpSettings.use_tls}
                  onCheckedChange={(checked) => setSmtpSettings({ ...smtpSettings, use_tls: checked })}
                />
                <Label htmlFor="use_tls">Use TLS/SSL (Recommended)</Label>
              </div>
              
              <div className="flex gap-3">
                <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-smtp-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Settings
                </Button>
                <Button variant="outline" onClick={handleTestSettings} disabled={testing} data-testid="test-smtp-btn">
                  {testing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Send className="w-4 h-4 mr-2" />
                  Send Test Email
                </Button>
              </div>
              
              {smtpSettings.configured && (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span>SMTP is configured and ready to send emails</span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Templates Tab */}
        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Email Templates
                  </CardTitle>
                  <CardDescription>Create reusable email templates for common messages</CardDescription>
                </div>
                <Button onClick={() => { setEditingTemplate(null); setTemplateForm({ name: '', subject: '', body: '', university_id: null, template_type: null }); setTemplateDialogOpen(true); }} data-testid="add-template-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Template
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No email templates yet</p>
                  <p className="text-sm">Create your first template to quickly send common emails</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>University</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {templates.map((template) => {
                      const typeInfo = TEMPLATE_TYPES.find(t => t.value === template.template_type);
                      const uni = universities.find(u => u.id === template.university_id);
                      return (
                        <TableRow key={template.id}>
                          <TableCell className="font-medium">{template.name}</TableCell>
                          <TableCell>
                            {typeInfo ? (
                              <Badge variant="secondary">{typeInfo.label}</Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {uni ? (
                              <Badge variant="outline">{uni.name}</Badge>
                            ) : (
                              <span className="text-muted-foreground">All Universities</span>
                            )}
                          </TableCell>
                          <TableCell>{template.subject}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditTemplate(template)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteTemplate(template.id)}>
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sent Emails History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Sent Emails History
              </CardTitle>
              <CardDescription>View all emails sent from your organization</CardDescription>
            </CardHeader>
            <CardContent>
              {sentEmails.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Send className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No emails sent yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sentEmails.map((email) => (
                      <TableRow key={email.id}>
                        <TableCell className="text-sm">
                          {new Date(email.sent_at).toLocaleString()}
                        </TableCell>
                        <TableCell>{email.to_email}</TableCell>
                        <TableCell>{email.subject}</TableCell>
                        <TableCell>
                          <Badge variant={email.status === 'sent' ? 'default' : 'destructive'}>
                            {email.status === 'sent' ? (
                              <><CheckCircle className="w-3 h-3 mr-1" /> Sent</>
                            ) : (
                              <><XCircle className="w-3 h-3 mr-1" /> Failed</>
                            )}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="sm:max-w-[760px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Email Template'}</DialogTitle>
            <DialogDescription>
              Create a reusable email template. Use placeholders like {'{{student_name}}'}, {'{{university_name}}'}, {'{{upload_link}}'}, {'{{missing_documents}}'} for dynamic content.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="template_type">Template Type</Label>
                <select
                  id="template_type"
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={templateForm.template_type || ''}
                  onChange={(e) => setTemplateForm({ ...templateForm, template_type: e.target.value || null })}
                >
                  <option value="">Select type (optional)</option>
                  {TEMPLATE_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label} - {t.description}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="template_university">University (Optional)</Label>
                <select
                  id="template_university"
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={templateForm.university_id || ''}
                  onChange={(e) => setTemplateForm({ ...templateForm, university_id: e.target.value || null })}
                >
                  <option value="">All Universities (Default)</option>
                  {universities.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="template_name">Template Name *</Label>
              <Input
                id="template_name"
                placeholder="e.g., Document Reminder"
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="template_subject">Email Subject *</Label>
                <select
                  className="h-8 px-2 text-xs rounded-md border border-input bg-background"
                  value=""
                  onChange={(e) => { insertVariable('subject', e.target.value); e.target.value = ''; }}
                  data-testid="template-subject-variable-picker"
                >
                  <option value="">+ Insert variable</option>
                  {TEMPLATE_VARIABLES.map((v) => (
                    <option key={v.key} value={v.key}>{v.label} → {'{{' + v.key + '}}'}</option>
                  ))}
                </select>
              </div>
              <Input
                id="template_subject"
                ref={subjectRef}
                placeholder="e.g., Reminder: Missing Documents for Your Application"
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="template_body">Email Body * (HTML supported)</Label>
                <select
                  className="h-8 px-2 text-xs rounded-md border border-input bg-background"
                  value=""
                  onChange={(e) => { insertVariable('body', e.target.value); e.target.value = ''; }}
                  data-testid="template-body-variable-picker"
                >
                  <option value="">+ Insert variable</option>
                  {TEMPLATE_VARIABLES.map((v) => (
                    <option key={v.key} value={v.key}>{v.label} → {'{{' + v.key + '}}'}</option>
                  ))}
                </select>
              </div>
              <Textarea
                id="template_body"
                ref={bodyRef}
                placeholder="Dear {{student_name}},&#10;&#10;This is a reminder about your application...&#10;&#10;Click here to upload documents: {{upload_link}}&#10;&#10;Missing documents:&#10;{{missing_documents}}"
                rows={10}
                value={templateForm.body}
                onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Click <strong>+ Insert variable</strong> to drop a placeholder at your cursor. Common placeholders:{' '}
                {'{{student_name}}'}, {'{{app_number}}'}, {'{{university_name}}'}, {'{{status}}'}, {'{{today}}'}, {'{{upload_link}}'}, {'{{missing_documents}}'}
              </p>
            </div>

            {/* r66.12 — Trigger Status Picker -------------------------------- */}
            <div className="space-y-2 border-t pt-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="text-sm font-semibold">
                  Auto-send when status changes to:
                </Label>
                {statusOptions.length > 0 && (
                  <div className="flex items-center gap-1 text-xs">
                    <button
                      type="button"
                      onClick={() => setTemplateForm((prev) => ({
                        ...prev,
                        trigger_status_ids: statusOptions.map((s) => s.id),
                        trigger_status_names: statusOptions.map((s) => s.name),
                      }))}
                      className="px-2 py-0.5 rounded text-emerald-700 hover:bg-emerald-50"
                      data-testid="trigger-status-all"
                    >
                      Select all
                    </button>
                    <span className="text-slate-300">·</span>
                    <button
                      type="button"
                      onClick={() => setTemplateForm((prev) => ({
                        ...prev, trigger_status_ids: [], trigger_status_names: [],
                      }))}
                      className="px-2 py-0.5 rounded text-slate-600 hover:bg-slate-100"
                      data-testid="trigger-status-clear"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Pick one or more statuses (sourced from <strong>Master Data → Application Status</strong> — same list used in the application detail header). Whenever a counsellor updates an application to a selected status, this template is automatically e-mailed to the student.
              </p>
              {statusOptions.length === 0 ? (
                <div
                  className="rounded-md bg-slate-50 p-3 border border-slate-200 text-xs text-muted-foreground italic"
                  data-testid="trigger-status-list"
                >
                  No application statuses configured yet. Add them in Master Data → Application Status.
                </div>
              ) : (
                <div data-testid="trigger-status-list">
                  <MultiSearchableSelect
                    options={statusOptions.map((s) => ({ value: s.id, label: s.name }))}
                    value={templateForm.trigger_status_ids || []}
                    onValueChange={(newIds) => {
                      const nameById = new Map(statusOptions.map((s) => [s.id, s.name]));
                      setTemplateForm((prev) => ({
                        ...prev,
                        trigger_status_ids: newIds,
                        trigger_status_names: newIds.map((id) => nameById.get(id)).filter(Boolean),
                      }));
                    }}
                    placeholder="Select one or more statuses…"
                    searchPlaceholder="Search statuses…"
                    emptyText="No matching status."
                    data-testid="trigger-status-dropdown"
                  />
                </div>
              )}
              {(templateForm.trigger_status_names || []).length > 0 && (
                <p className="text-[11px] text-emerald-700 pt-1">
                  Will auto-send on: <strong>{templateForm.trigger_status_names.join(', ')}</strong>
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveTemplate}>
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SMTPSettingsPage;
